import { AfterViewInit, Component, DestroyRef, effect, ElementRef, inject, input, model, OnInit, Renderer2, viewChildren } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormDisplayControl } from '@oi-soft/common';
import { FormValidationComponent } from '@shared/components/form/form-validation/form-validation.component';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-form-control-underline',
    imports: [ReactiveFormsModule, FormValidationComponent],
    templateUrl: './form-control-underline.component.html',
    styleUrl: './form-control-underline.component.scss'
})
export class FormControlUnderlineComponent implements AfterViewInit, OnInit{

    private destroyRef = inject(DestroyRef);
    renderer = inject(Renderer2);
    
    control = model.required<FormDisplayControl>();
    length = input.required<number>();
    type = input<string>('text');
    
    form = new FormGroup({});
    controlNames: string[] = [];
    inputFields = viewChildren<ElementRef<HTMLInputElement>>('input');
        
    subscriptions:Subscription[] = [];

    constructor(){
        //Effect to sync real value with inputFields (especially when real value is changed programmatically outside this component)
        effect(() => {
            this.subscriptions[0]?.unsubscribe();

            const mainControl = this.control();

            if(mainControl){
                this.subscriptions[0] = mainControl.valueChanges.subscribe({
                    next:(value:string) => {

                        let inputFieldsValue = '';

                        this.inputFields().forEach((inputRef) => {
                            inputFieldsValue += inputRef.nativeElement.value
                        })

                        //Sync both fields if they are not.
                        if(value != inputFieldsValue){
                            let newValue = value;

                            if(newValue){
                                if(newValue.length > this.length()){
                                    newValue = newValue.substring(0, this.length());
                                }

                                this.inputFields().forEach((inputRef, index) => {
                                    inputRef.nativeElement.value = newValue[index];
                                })
                            }
                            else{
                                this.inputFields().forEach((inputRef) => {
                                    inputRef.nativeElement.value = '';
                                })
                            }

                            this.control().setValue(newValue, {emitEvent: false});
                            this.form.reset();
                        }
                    }
                })

                this.destroyRef.onDestroy(() => {
                    this.subscriptions[0]?.unsubscribe();
                })
            }
        })
    }
    
    ngOnInit(): void {
        
        //Add inputs dynamically
        for (let i = 0; i < this.length(); i++){
            let controlName = 'char' + i;
            this.form.addControl(controlName, new FormControl('',{validators:[Validators.required]}));
            this.controlNames.push(controlName);
        }
        
    }
    
    ngAfterViewInit(): void {
        
        //Add Functionality to the inputs.
        this.inputFields().forEach((inputRef, index) => {
            
            const controlInputUnlisten = this.renderer.listen(inputRef.nativeElement, 'input', () => {
                
                this.control().markAsDirty();
                
                if(inputRef.nativeElement.value.length == inputRef.nativeElement.maxLength){
                    
                    const nextInput = this.inputFields()[index + 1];
                    
                    if (nextInput) {
                        nextInput.nativeElement.focus();
                    }
                    
                }
                
                let newValue = '';
                
                this.inputFields().forEach((inputRef) => {
                    newValue += inputRef.nativeElement.value
                })

                this.control().setValue(newValue);
                
            })
            
            const controlKeydownUnlisten = this.renderer.listen(inputRef.nativeElement, 'keydown', (event) => {
                
                if(inputRef.nativeElement.value.length == 0 && event.key == 'Backspace'){
                    
                    const nextInput = this.inputFields()[index - 1];
                    
                    if (nextInput) {
                        nextInput.nativeElement.focus();
                    }
                    
                }
                
            })
            
            const controlFocusoutUnlisten = this.renderer.listen(inputRef.nativeElement, 'focusout', (event) => {
                
                this.control().markAsTouched();
                
            })
            
            const controlPasteUnlisten = this.renderer.listen(inputRef.nativeElement, 'paste', (event) => {
                
                event.preventDefault();
                
                let clipboardText = event.clipboardData?.getData('text');
                
                for (let i = 0; i < clipboardText!.length; i++){
                    
                    const activeElement = document.activeElement as HTMLInputElement;
                    
                    if (activeElement && clipboardText) {
                        
                        if(activeElement.value){
                            return;
                        }
                        
                        activeElement.value = clipboardText[i];
                        activeElement.dispatchEvent(new Event('input'));
                    }
                    
                }
                
            })
            
            this.destroyRef.onDestroy(() => {
                controlInputUnlisten();
                controlKeydownUnlisten();
                controlFocusoutUnlisten();
                controlPasteUnlisten();
            })
            
        })
        
    }
    
    
}
